import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { aclAPI, ACLPermission, ACLPermissionListMeta, getPermissionActions } from '../../api/aclApi';
import { clearPermissionCache } from '../../acl/usePermission';
import { ActionTypes } from '@/actions';
import { dataAPI } from '../../api/dataApi';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { ArrowDown, ArrowUp, ArrowUpDown, Pencil, RefreshCw, Trash2 } from 'lucide-react';
import { PulseLoader } from 'react-spinners';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { resolveApiBaseUrl } from '../../api/apiBase';

const API_BASE_URL = resolveApiBaseUrl();

const ACTION_BADGE_LABELS: Record<string, string> = {
    view: 'view',
    list: 'list collections',
    listItems: 'list items',
    create: 'create',
    create_collection: 'create collection',
    read: 'read',
    update: 'update',
    delete: 'delete',
    delegate: 'delegate',
    field_read: 'field read',
    field_update: 'field update',
    edit_collection_name: 'edit name',
    edit_collection_name_suggestion: 'suggest name',
    view_collection_advanced: 'view advanced',
    relate: 'relate (legacy)',
    public_export: 'public export (legacy)',
    publish: 'publish (legacy)',
    archive: 'archive (legacy)',
    share: 'share (legacy)',
    public_publish_collection: 'public publish',
    public_expose_fields: 'public fields',
    public_form: 'public form',
    all_users_access_collection: 'all users access',
    all_users_expose_fields: 'all users fields',
    create_form: 'create form config',
    read_form: 'read form config',
    update_form: 'update form config',
    delete_form: 'delete form config',
};

interface PermissionListProps {
    onEdit: (permission: ACLPermission) => void;
    onRefresh?: () => void;
    refreshKey?: number;
}

type SortKey = 'whoType' | 'who' | 'action' | 'whatType' | 'what' | 'fields' | 'created' | 'updated' | 'audit' | 'redundant';

export const PermissionList: React.FC<PermissionListProps> = ({ onEdit, onRefresh, refreshKey }) => {
    const dispatch = useDispatch();
    const [permissions, setPermissions] = useState<ACLPermission[]>([]);
    const [listMeta, setListMeta] = useState<ACLPermissionListMeta | null>(null);
    const [loading, setLoading] = useState(true);
    const [selectedUids, setSelectedUids] = useState<Set<string>>(() => new Set());
    const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [permissionToDelete, setPermissionToDelete] = useState<ACLPermission | null>(null);
    const [sortKey, setSortKey] = useState<SortKey>('whoType');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [userEmailById, setUserEmailById] = useState<Record<string, string>>({});
    const [filters, setFilters] = useState({
        whoType: '',
        who: '',
        action: '',
        whatType: '',
        what: '',
        fields: '',
        redundant: '' as '' | 'redundant' | 'non_redundant',
    });
    const [componentLabelById, setComponentLabelById] = useState<Record<string, string>>({});
    const [groupTitleById, setGroupTitleById] = useState<Record<string, string>>({});

    const loadPermissions = useCallback(async () => {
        try {
            setLoading(true);
            const { data, meta } = await aclAPI.getAllPermissions({ includeRedundancy: true });
            setPermissions(data);
            setListMeta(meta ?? null);
            setSelectedUids(prev => {
                const allowed = new Set(data.map(p => p.uid));
                return new Set([...prev].filter(uid => allowed.has(uid)));
            });
        } catch (error: any) {
            console.error('Error loading permissions:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPermissions();
    }, [refreshKey, loadPermissions]);

    useEffect(() => {
        let isMounted = true;
        const loadUserEmails = async () => {
            try {
                const response = await dataAPI.getCollectionItems('users', { limit: 500 });
                const users = Array.isArray(response?.data) ? response.data : [];
                const emailMap = users.reduce((acc: Record<string, string>, user: any) => {
                    const uid = user?.uid || user?.id || user?._id;
                    const email = user?.email;
                    if (uid && email) {
                        acc[String(uid)] = String(email);
                    }
                    return acc;
                }, {});
                if (isMounted) {
                    setUserEmailById(emailMap);
                }
            } catch (error) {
                console.error('Error loading user emails:', error);
            }
        };

        loadUserEmails();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        const loadGroups = async () => {
            try {
                const token = localStorage.getItem('authToken') || localStorage.getItem('idToken');
                if (!token) return;
                const response = await fetch(`${API_BASE_URL}/auth/admin/groups`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                if (!response.ok) return;
                const payload = await response.json();
                const groups = Array.isArray(payload?.groups) ? payload.groups : [];
                const map = groups.reduce((acc: Record<string, string>, group: any) => {
                    const uid = typeof group?.uid === 'string' ? group.uid.trim() : '';
                    const title = typeof group?.title === 'string' ? group.title.trim() : '';
                    if (uid && title) {
                        acc[uid] = title;
                    }
                    return acc;
                }, {});
                if (isMounted) {
                    setGroupTitleById(map);
                }
            } catch (error) {
                console.error('Error loading groups:', error);
            }
        };

        loadGroups();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        const loadComponents = async () => {
            try {
                const response = await dataAPI.getComponents();
                const rawComponents = response?.data || response?.components || [];
                const components = Array.isArray(rawComponents) ? rawComponents : [];
                const labelMap = components.reduce((acc: Record<string, string>, component: any) => {
                    const uid = component?.uid || component?.id || component?._id;
                    const slug = component?.slug;
                    const name = component?.name || slug || uid;
                    if (uid && name) {
                        acc[String(uid)] = String(name);
                    }
                    if (slug && name) {
                        acc[String(slug)] = String(name);
                    }
                    return acc;
                }, {});
                if (isMounted) {
                    setComponentLabelById(labelMap);
                }
            } catch (error) {
                console.error('Error loading components:', error);
            }
        };

        loadComponents();

        return () => {
            isMounted = false;
        };
    }, []);

    const handleDelete = async () => {
        if (!permissionToDelete) return;

        try {
            await aclAPI.deletePermission(permissionToDelete.uid);
            clearPermissionCache();
            dispatch({ type: ActionTypes.UI_RELOAD_CONFIG });
            setPermissions(permissions.filter(p => p.uid !== permissionToDelete.uid));
            setSelectedUids(prev => {
                const next = new Set(prev);
                next.delete(permissionToDelete.uid);
                return next;
            });
            setDeleteDialogOpen(false);
            setPermissionToDelete(null);
            onRefresh?.();
        } catch (error: any) {
            console.error('Error deleting permission:', error);
            alert('Failed to delete permission: ' + error.message);
        }
    };

    const toggleSelectUid = (uid: string) => {
        setSelectedUids(prev => {
            const next = new Set(prev);
            if (next.has(uid)) {
                next.delete(uid);
            } else {
                next.add(uid);
            }
            return next;
        });
    };

    const openDeleteDialog = (permission: ACLPermission) => {
        setPermissionToDelete(permission);
        setDeleteDialogOpen(true);
    };

    const getWhoTypeColor = (whoType: string) => {
        switch (whoType) {
            case 'UID': return 'bg-blue-100 text-blue-800';
            case 'GROUPID': return 'bg-green-100 text-green-800';
            case 'ROLE': return 'bg-purple-100 text-purple-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getWhoTypeLabel = (whoType?: string) => {
        if (whoType === 'UID') return 'User';
        if (whoType === 'GROUPID') return 'Group';
        return whoType || '';
    };

    const getActionColor = (action: string) => {
        if (action.includes('delete')) return 'bg-red-100 text-red-800';
        if (action.includes('create')) return 'bg-green-100 text-green-800';
        if (action.includes('update')) return 'bg-yellow-100 text-yellow-800';
        if (action.includes('read') || action.includes('view')) return 'bg-blue-100 text-blue-800';
        return 'bg-gray-100 text-gray-800';
    };

    const getActionTooltip = (action?: string) => {
        if (!action) return 'No action specified.';
        if (action === 'public_publish_collection') {
            return 'Public audience: grants full collection payload exposure in public endpoints.';
        }
        if (action === 'public_expose_fields') {
            return 'Public audience: grants field-level exposure for selected fields.';
        }
        if (action === 'public_form') {
            return 'Public audience: allows published public forms to use this collection (fields per form config); not for generic public feeds.';
        }
        if (action === 'all_users_access_collection') {
            return 'All authenticated users: grants full collection access for logged-in users.';
        }
        if (action === 'all_users_expose_fields') {
            return 'All authenticated users: grants field-level access for selected fields.';
        }
        if (action === 'create_form') {
            return 'Create public form configs (public_forms). Independent of collection item CRUD.';
        }
        if (action === 'read_form') {
            return 'Read public form configs. Independent of collection item read access.';
        }
        if (action === 'update_form') {
            return 'Update public form configs. Independent of collection item update access.';
        }
        if (action === 'delete_form') {
            return 'Delete public form configs. Independent of collection item delete access.';
        }
        if (action === 'list') {
            return 'Collection shell visibility only (name/count). Table rows still require view.';
        }
        const delimiterMatch = action.match(/[:.]/);
        const delimiterIndex = delimiterMatch?.index ?? -1;
        const rawResource = delimiterIndex >= 0 ? action.slice(delimiterIndex + 1) : '';
        const verb = (delimiterIndex >= 0 ? action.slice(0, delimiterIndex) : action).toLowerCase();
        const resource = rawResource.replace(/[_-]+/g, ' ').trim();

        const baseDescription: Record<string, string> = {
            create: 'Allows creating new items',
            create_collection: 'Allows creating new collections',
            list: 'Allows listing a collection',
            listitems: 'Allows enumerating or querying items in a collection',
            relate: 'Allows using items in relation fields',
            read: 'Allows viewing an item',
            view: 'Allows viewing an item',
            update: 'Allows editing an item',
            delete: 'Allows deleting an item',
        };

        const description = baseDescription[verb];
        if (!description) {
            return `Custom action: ${action}.`;
        }
        if (!resource) {
            return `${description}.`;
        }
        return `${description} in ${resource}.`;
    };

    const toggleSort = (key: SortKey) => {
        if (key === sortKey) {
            setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
            return;
        }
        setSortKey(key);
        setSortDirection(key === 'redundant' ? 'desc' : 'asc');
    };

    const compareValues = (a: string | number, b: string | number) => {
        if (typeof a === 'number' && typeof b === 'number') {
            return a - b;
        }
        return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
    };

    const whoTypeOptions = useMemo(() => {
        return Array.from(new Set(permissions.map(permission => permission.whoType).filter(Boolean))).sort();
    }, [permissions]);

    const whatTypeOptions = useMemo(() => {
        return Array.from(new Set(permissions.map(permission => permission.whatType).filter(Boolean))).sort();
    }, [permissions]);

    const hasFilters = useMemo(() => {
        return Object.entries(filters).some(([k, value]) =>
            k === 'redundant' ? value !== '' : String(value).trim().length > 0
        );
    }, [filters]);


    const formatDateTime = (value?: string) => {
        if (!value) return 'n/a';
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) {
            return value;
        }
        return parsed.toLocaleString();
    };

    const getUserLabel = (uid?: string) => {
        if (!uid) return '';
        return userEmailById[uid] || uid;
    };

    const getGroupLabel = (rawWho?: string) => {
        if (!rawWho) return '';
        const trimmed = String(rawWho).trim();
        if (groupTitleById[trimmed]) {
            return groupTitleById[trimmed];
        }
        const separator = ' • ';
        if (trimmed.includes(separator)) {
            return trimmed.split(separator)[0].trim();
        }
        return trimmed;
    };

    const isCollectionWhatType = (whatType?: string) => {
        return whatType === 'CollectionId'
            || whatType === 'CollectionIdSet'
            || whatType === 'AllItemsInCollectionId';
    };

    const getWhatIds = (permission: ACLPermission) => {
        if (Array.isArray(permission.what)) {
            return permission.what.map(value => String(value));
        }
        if (permission.what) {
            return [String(permission.what)];
        }
        return [];
    };

    const getComponentLabel = (value?: string) => {
        if (!value) return '';
        return componentLabelById[value] || value;
    };

    const getWhatLabels = (permission: ACLPermission) => {
        const acts = getPermissionActions(permission);
        if (acts.includes('create_collection') && permission.what === '*') {
            return ['New collection (any)'];
        }
        const ids = getWhatIds(permission);
        if (ids.length === 0) return [];
        if (isCollectionWhatType(permission.whatType)) {
            return ids.map(id => getComponentLabel(id));
        }
        return ids;
    };

    const getWhatTypeDisplay = (permission: ACLPermission) => {
        const acts = getPermissionActions(permission);
        if (acts.includes('create_collection') && permission.what === '*') {
            return 'New collection (any)';
        }
        return permission.whatType ?? '';
    };

    const getWhatSortValue = (permission: ACLPermission) => {
        const labels = getWhatLabels(permission);
        if (labels.length === 0) return '';
        return labels.join(', ');
    };

    const getSortValue = (permission: ACLPermission, key: SortKey) => {
        switch (key) {
            case 'whoType':
                return permission.whoType ?? '';
            case 'who':
                if (permission.whoType === 'UID') {
                    return userEmailById[permission.who] || permission.who || '';
                }
                if (permission.whoType === 'GROUPID') {
                    return getGroupLabel(permission.who);
                }
                return permission.who ?? '';
            case 'action':
                return getPermissionActions(permission).sort().join(', ');
            case 'redundant':
                return permission.redundancy?.redundant ? 1 : 0;
            case 'whatType':
                return permission.whatType ?? '';
            case 'what':
                return getWhatSortValue(permission);
            case 'fields': {
                return permission.includeFields?.length ?? 0;
            }
            case 'created':
                return permission.created ? Date.parse(permission.created) : 0;
            case 'updated':
                return permission.updated ? Date.parse(permission.updated) : 0;
            case 'audit': {
                const updatedBy = permission.updatedBy ? getUserLabel(permission.updatedBy) : '';
                const createdBy = permission.createdBy ? getUserLabel(permission.createdBy) : '';
                return [updatedBy, createdBy].filter(Boolean).join(' ');
            }
            default:
                return '';
        }
    };

    const sortedPermissions = useMemo(() => {
        const sorted = [...permissions];
        const direction = sortDirection === 'asc' ? 1 : -1;
        sorted.sort((a, b) => {
            const valueA = getSortValue(a, sortKey);
            const valueB = getSortValue(b, sortKey);
            const result = compareValues(valueA, valueB);
            if (result !== 0) return result * direction;
            return compareValues(a.uid, b.uid);
        });
        return sorted;
    }, [permissions, sortDirection, sortKey]);

    const filteredPermissions = useMemo(() => {
        const normalize = (value: string) => value.toLowerCase();
        const matchesText = (value: string | undefined, filter: string) => {
            if (!filter) return true;
            return normalize(String(value ?? '')).includes(normalize(filter));
        };

        return sortedPermissions.filter(permission => {
            if (filters.whoType && permission.whoType !== filters.whoType) return false;
            if (filters.whatType && permission.whatType !== filters.whatType) return false;
            if (filters.redundant === 'redundant' && !permission.redundancy?.redundant) return false;
            if (filters.redundant === 'non_redundant' && permission.redundancy?.redundant) return false;

            const whoLabel = permission.whoType === 'UID'
                ? `${getUserLabel(permission.who)} ${permission.who ?? ''}`
                : permission.whoType === 'GROUPID'
                    ? `${getGroupLabel(permission.who)} ${permission.who ?? ''}`
                    : permission.who ?? '';
            if (!matchesText(whoLabel, filters.who)) return false;

            const actionLabel = getPermissionActions(permission).join(' ');
            if (!matchesText(actionLabel, filters.action)) return false;

            const whatLabels = getWhatLabels(permission).join(', ');
            const whatIds = getWhatIds(permission).join(', ');
            const whatValue = [whatLabels, whatIds].filter(Boolean).join(' ');
            if (!matchesText(whatValue, filters.what)) return false;

            const fieldsValue = [...(permission.includeFields || [])].join(', ');
            if (!matchesText(fieldsValue, filters.fields)) return false;

            return true;
        });
    }, [filters, sortedPermissions, userEmailById]);

    const allFilteredSelected =
        filteredPermissions.length > 0 &&
        filteredPermissions.every(p => selectedUids.has(p.uid));
    const someFilteredSelected = filteredPermissions.some(p => selectedUids.has(p.uid));

    const toggleSelectAllFiltered = () => {
        setSelectedUids(prev => {
            const next = new Set(prev);
            if (
                filteredPermissions.length > 0 &&
                filteredPermissions.every(p => next.has(p.uid))
            ) {
                filteredPermissions.forEach(p => next.delete(p.uid));
            } else {
                filteredPermissions.forEach(p => next.add(p.uid));
            }
            return next;
        });
    };

    const selectedCount = selectedUids.size;

    const handleBulkDelete = async () => {
        const ids = [...selectedUids];
        if (ids.length === 0) return;
        setBulkDeleting(true);
        const failures: string[] = [];
        for (const uid of ids) {
            try {
                await aclAPI.deletePermission(uid);
            } catch (err: any) {
                failures.push(`${uid}: ${err?.message ?? 'failed'}`);
            }
        }
        setBulkDeleting(false);
        setBulkDeleteDialogOpen(false);
        if (failures.length < ids.length) {
            clearPermissionCache();
            dispatch({ type: ActionTypes.UI_RELOAD_CONFIG });
        }
        await loadPermissions();
        onRefresh?.();
        if (failures.length > 0) {
            alert(
                `Removed ${ids.length - failures.length} of ${ids.length} permission(s). Failures:\n${failures.slice(0, 12).join('\n')}${failures.length > 12 ? '\n…' : ''}`
            );
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center p-8">
                <PulseLoader color="#3b82f6" />
            </div>
        );
    }

    return (
        <>
            <div className="space-y-4">
                <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                    <h2 className="text-2xl font-bold">
                        Permissions ({hasFilters ? `${filteredPermissions.length} of ${permissions.length}` : permissions.length})
                        {selectedCount > 0 && (
                            <span className="ml-2 text-base font-normal text-gray-600 dark:text-gray-400">
                                ({selectedCount} selected)
                            </span>
                        )}
                    </h2>
                    <div className="flex flex-wrap items-center gap-2">
                        {selectedCount > 0 && (
                            <>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setSelectedUids(new Set())}
                                >
                                    Clear selection
                                </Button>
                                <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => setBulkDeleteDialogOpen(true)}
                                >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete selected ({selectedCount})
                                </Button>
                            </>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={loadPermissions}
                        >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Refresh
                        </Button>
                    </div>
                </div>

                <div className="border rounded-lg p-4 bg-white dark:bg-gray-900">
                    <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
                        <div className="space-y-1">
                            <span className="text-xs font-medium text-gray-500">Who Type</span>
                            <Select
                                value={filters.whoType || 'all'}
                                onValueChange={(value) => {
                                    setFilters(prev => ({ ...prev, whoType: value === 'all' ? '' : value }));
                                }}
                            >
                                <SelectTrigger className="h-9">
                                    <SelectValue placeholder="All" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All</SelectItem>
                                    {whoTypeOptions.map(option => (
                                        <SelectItem key={option} value={option}>{getWhoTypeLabel(option)}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <span className="text-xs font-medium text-gray-500">Who</span>
                            <Input
                                value={filters.who}
                                onChange={(event) => setFilters(prev => ({ ...prev, who: event.target.value }))}
                                placeholder="Search who..."
                                className="h-9"
                            />
                        </div>
                        <div className="space-y-1">
                            <span className="text-xs font-medium text-gray-500">Action</span>
                            <Input
                                value={filters.action}
                                onChange={(event) => setFilters(prev => ({ ...prev, action: event.target.value }))}
                                placeholder="Search action..."
                                className="h-9"
                            />
                        </div>
                        <div className="space-y-1">
                            <span className="text-xs font-medium text-gray-500">What Type</span>
                            <Select
                                value={filters.whatType || 'all'}
                                onValueChange={(value) => {
                                    setFilters(prev => ({ ...prev, whatType: value === 'all' ? '' : value }));
                                }}
                            >
                                <SelectTrigger className="h-9">
                                    <SelectValue placeholder="All" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All</SelectItem>
                                    {whatTypeOptions.map(option => (
                                        <SelectItem key={option} value={option}>{option}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <span className="text-xs font-medium text-gray-500">What</span>
                            <Input
                                value={filters.what}
                                onChange={(event) => setFilters(prev => ({ ...prev, what: event.target.value }))}
                                placeholder="Search what..."
                                className="h-9"
                            />
                        </div>
                        <div className="space-y-1">
                            <span className="text-xs font-medium text-gray-500">Fields</span>
                            <Input
                                value={filters.fields}
                                onChange={(event) => setFilters(prev => ({ ...prev, fields: event.target.value }))}
                                placeholder="Search fields..."
                                className="h-9"
                            />
                        </div>
                        <div className="space-y-1">
                            <span className="text-xs font-medium text-gray-500">Redundancy</span>
                            <Select
                                value={filters.redundant || 'all'}
                                onValueChange={(value) => {
                                    setFilters(prev => ({
                                        ...prev,
                                        redundant: value === 'all' ? '' : (value as 'redundant' | 'non_redundant'),
                                    }));
                                }}
                            >
                                <SelectTrigger className="h-9">
                                    <SelectValue placeholder="All" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All</SelectItem>
                                    <SelectItem value="redundant">Redundant only</SelectItem>
                                    <SelectItem value="non_redundant">Non-redundant</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="flex justify-end mt-3">
                        <Button
                            variant="ghost"
                            size="sm"
                            disabled={!hasFilters}
                            onClick={() => setFilters({
                                whoType: '',
                                who: '',
                                action: '',
                                whatType: '',
                                what: '',
                                fields: '',
                                redundant: '',
                            })}
                        >
                            Clear filters
                        </Button>
                    </div>
                </div>

                {permissions.length === 0 ? (
                    <Card>
                        <CardContent className="pt-6 text-center text-gray-500">
                            No permissions found. Create one to get started.
                        </CardContent>
                    </Card>
                ) : (
                    <TooltipProvider>
                        <div className="border rounded-lg overflow-hidden">
                            <div className="max-h-[60vh] overflow-y-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-gray-50 dark:bg-gray-800">
                                            <TableHead className="w-10 font-semibold">
                                                <span className="sr-only">Select</span>
                                                <Checkbox
                                                    aria-label="Select all visible permissions"
                                                    checked={
                                                        allFilteredSelected
                                                            ? true
                                                            : someFilteredSelected
                                                                ? 'indeterminate'
                                                                : false
                                                    }
                                                    disabled={filteredPermissions.length === 0}
                                                    onCheckedChange={() => toggleSelectAllFiltered()}
                                                />
                                            </TableHead>
                                            <TableHead className="font-semibold">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => toggleSort('whoType')}
                                                    className="h-8 px-2 -ml-2 flex items-center gap-2"
                                                >
                                                    Who Type
                                                    {sortKey === 'whoType'
                                                        ? (sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />)
                                                        : <ArrowUpDown className="h-4 w-4" />}
                                                </Button>
                                            </TableHead>
                                            <TableHead className="font-semibold">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => toggleSort('who')}
                                                    className="h-8 px-2 -ml-2 flex items-center gap-2"
                                                >
                                                    Who
                                                    {sortKey === 'who'
                                                        ? (sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />)
                                                        : <ArrowUpDown className="h-4 w-4" />}
                                                </Button>
                                            </TableHead>
                                            <TableHead className="font-semibold">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => toggleSort('action')}
                                                    className="h-8 px-2 -ml-2 flex items-center gap-2"
                                                >
                                                    Action
                                                    {sortKey === 'action'
                                                        ? (sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />)
                                                        : <ArrowUpDown className="h-4 w-4" />}
                                                </Button>
                                            </TableHead>
                                            <TableHead className="font-semibold">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => toggleSort('whatType')}
                                                    className="h-8 px-2 -ml-2 flex items-center gap-2"
                                                >
                                                    What Type
                                                    {sortKey === 'whatType'
                                                        ? (sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />)
                                                        : <ArrowUpDown className="h-4 w-4" />}
                                                </Button>
                                            </TableHead>
                                            <TableHead className="font-semibold">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => toggleSort('what')}
                                                    className="h-8 px-2 -ml-2 flex items-center gap-2"
                                                >
                                                    What
                                                    {sortKey === 'what'
                                                        ? (sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />)
                                                        : <ArrowUpDown className="h-4 w-4" />}
                                                </Button>
                                            </TableHead>
                                            <TableHead className="font-semibold">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => toggleSort('fields')}
                                                    className="h-8 px-2 -ml-2 flex items-center gap-2"
                                                >
                                                    Fields
                                                    {sortKey === 'fields'
                                                        ? (sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />)
                                                        : <ArrowUpDown className="h-4 w-4" />}
                                                </Button>
                                            </TableHead>
                                            <TableHead className="font-semibold">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => toggleSort('created')}
                                                    className="h-8 px-2 -ml-2 flex items-center gap-2"
                                                >
                                                    Created
                                                    {sortKey === 'created'
                                                        ? (sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />)
                                                        : <ArrowUpDown className="h-4 w-4" />}
                                                </Button>
                                            </TableHead>
                                            <TableHead className="font-semibold">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => toggleSort('updated')}
                                                    className="h-8 px-2 -ml-2 flex items-center gap-2"
                                                >
                                                    Updated
                                                    {sortKey === 'updated'
                                                        ? (sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />)
                                                        : <ArrowUpDown className="h-4 w-4" />}
                                                </Button>
                                            </TableHead>
                                            <TableHead className="font-semibold">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => toggleSort('audit')}
                                                    className="h-8 px-2 -ml-2 flex items-center gap-2"
                                                >
                                                    Audit
                                                    {sortKey === 'audit'
                                                        ? (sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />)
                                                        : <ArrowUpDown className="h-4 w-4" />}
                                                </Button>
                                            </TableHead>
                                            <TableHead className="font-semibold">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => toggleSort('redundant')}
                                                    className="h-8 px-2 -ml-2 flex items-center gap-2"
                                                >
                                                    Redundant
                                                    {sortKey === 'redundant'
                                                        ? (sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />)
                                                        : <ArrowUpDown className="h-4 w-4" />}
                                                </Button>
                                            </TableHead>
                                            <TableHead className="font-semibold text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filteredPermissions.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={12} className="text-center text-sm text-gray-500 py-6">
                                                    No permissions match the current filters.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {filteredPermissions.map((permission) => (
                                            <TableRow
                                                key={permission.uid}
                                                className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                                                onDoubleClick={() => onEdit(permission)}
                                            >
                                                <TableCell
                                                    className="w-10 align-middle"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <Checkbox
                                                        aria-label={`Select permission ${permission.uid}`}
                                                        checked={selectedUids.has(permission.uid)}
                                                        onCheckedChange={() => toggleSelectUid(permission.uid)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Badge className={getWhoTypeColor(permission.whoType)}>
                                                        {getWhoTypeLabel(permission.whoType)}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    {permission.whoType === 'UID' ? (
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <div className="flex flex-col">
                                                                    <span className="text-sm">
                                                                        {getUserLabel(permission.who)}
                                                                    </span>
                                                                    <span className="text-xs text-muted-foreground font-mono">
                                                                        {permission.who}
                                                                    </span>
                                                                </div>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <span className="font-mono text-xs">{permission.who}</span>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    ) : permission.whoType === 'GROUPID' ? (
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <div className="flex flex-col">
                                                                    <span className="text-sm">{getGroupLabel(permission.who)}</span>
                                                                    <span className="text-xs text-muted-foreground font-mono">
                                                                        {permission.who}
                                                                    </span>
                                                                </div>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <span className="font-mono text-xs">{permission.who}</span>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    ) : (
                                                        <span className="font-mono text-sm">{permission.who}</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex flex-wrap gap-1">
                                                        {getPermissionActions(permission).map((act, i) => (
                                                            <Tooltip key={`${act}-${i}`}>
                                                                <TooltipTrigger>
                                                                    <Badge className={`${getActionColor(act)} cursor-help`}>
                                                                        {ACTION_BADGE_LABELS[act] || act}
                                                                    </Badge>
                                                                </TooltipTrigger>
                                                                <TooltipContent>{getActionTooltip(act)}</TooltipContent>
                                                            </Tooltip>
                                                        ))}
                                                        {getPermissionActions(permission).length === 0 && (
                                                            <span className="text-xs text-muted-foreground">—</span>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="outline">
                                                        {getWhatTypeDisplay(permission)}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    {isCollectionWhatType(permission.whatType) ? (
                                                        <Tooltip>
                                                            <TooltipTrigger>
                                                                <span className="text-sm">
                                                                    {getWhatLabels(permission).join(', ')}
                                                                </span>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <span className="font-mono text-xs">
                                                                    {getWhatIds(permission).join(', ')}
                                                                </span>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    ) : (
                                                        <span className="font-mono text-sm">
                                                            {getWhatIds(permission).join(', ')}
                                                        </span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex gap-1 flex-wrap">
                                                        {permission.includeFields && permission.includeFields.length > 0 && (
                                                            <Tooltip>
                                                                <TooltipTrigger>
                                                                    <Badge variant="secondary" className="bg-green-50 text-green-700 text-xs cursor-help">
                                                                        +{permission.includeFields.length}
                                                                    </Badge>
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    <div className="text-xs">
                                                                        <div className="font-semibold mb-1">Include fields:</div>
                                                                        <div className="font-mono">{permission.includeFields.join(', ')}</div>
                                                                    </div>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        )}
                                                        {(!permission.includeFields || permission.includeFields.length === 0) &&
                                                            (!permission.excludeFields || permission.excludeFields.length === 0) && (
                                                                <span className="text-gray-400 text-xs">All</span>
                                                            )}
                                                        {(!permission.includeFields || permission.includeFields.length === 0) &&
                                                            permission.excludeFields &&
                                                            permission.excludeFields.length > 0 && (
                                                                <Tooltip>
                                                                    <TooltipTrigger>
                                                                        <Badge variant="outline" className="text-xs cursor-help text-muted-foreground">
                                                                            Legacy exclude ({permission.excludeFields.length})
                                                                        </Badge>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        <div className="text-xs font-mono max-w-xs">
                                                                            {permission.excludeFields.join(', ')}
                                                                        </div>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            )}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="text-xs text-gray-600">
                                                        {formatDateTime(permission.created)}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="text-xs text-gray-600">
                                                        {formatDateTime(permission.updated)}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="text-xs text-gray-600 space-y-1">
                                                        {permission.createdBy && (
                                                            <div>
                                                                Created by:{' '}
                                                                <span className="font-mono">{getUserLabel(permission.createdBy)}</span>
                                                            </div>
                                                        )}
                                                        {permission.updatedBy && (
                                                            <div>
                                                                Updated by:{' '}
                                                                <span className="font-mono">{getUserLabel(permission.updatedBy)}</span>
                                                            </div>
                                                        )}
                                                        {!permission.createdBy && !permission.updatedBy && (
                                                            <span className="text-gray-400">—</span>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    {permission.redundancy?.redundant ? (
                                                        <Tooltip>
                                                            <TooltipTrigger>
                                                                <Badge variant="outline" className="border-amber-500 text-amber-800 dark:text-amber-200 cursor-help">
                                                                    Redundant
                                                                </Badge>
                                                            </TooltipTrigger>
                                                            <TooltipContent className="max-w-xs">
                                                                <div className="text-xs space-y-1">
                                                                    {permission.redundancy.reasonCode && (
                                                                        <div className="font-mono">{permission.redundancy.reasonCode}</div>
                                                                    )}
                                                                    {permission.redundancy.message && <div>{permission.redundancy.message}</div>}
                                                                    {permission.redundancy.supersedesBy && (
                                                                        <div className="text-muted-foreground">Ref: {permission.redundancy.supersedesBy}</div>
                                                                    )}
                                                                </div>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    ) : (
                                                        <span className="text-xs text-muted-foreground">—</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex gap-2 justify-end">
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => onEdit(permission)}
                                                                    className="h-8 w-8 p-0"
                                                                >
                                                                    <Pencil className="w-4 h-4" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>Edit permission</TooltipContent>
                                                        </Tooltip>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => openDeleteDialog(permission)}
                                                                    className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>Delete permission</TooltipContent>
                                                        </Tooltip>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                        {listMeta && (
                            <p className="text-xs text-muted-foreground mt-2 px-1">
                                Effective access hints use policy <span className="font-mono">{listMeta.policyModel}</span>
                                {' '}as of {formatDateTime(listMeta.generatedAt)} (advisory snapshot).
                            </p>
                        )}
                    </TooltipProvider>
                )}
            </div>

            <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={(open) => !bulkDeleting && setBulkDeleteDialogOpen(open)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete {selectedCount} permissions</AlertDialogTitle>
                        <AlertDialogDescription>
                            Permanently remove {selectedCount} selected permission{selectedCount === 1 ? '' : 's'}? This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                void handleBulkDelete();
                            }}
                            disabled={bulkDeleting}
                            className="bg-red-500 hover:bg-red-600"
                        >
                            {bulkDeleting ? 'Deleting…' : 'Delete all selected'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Permission</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete this permission?
                            {permissionToDelete && (
                                <div className="mt-2 p-2 bg-gray-100 rounded">
                                    <strong>{getWhoTypeLabel(permissionToDelete.whoType)}</strong> {permissionToDelete.who}
                                    {' '}<strong>{getPermissionActions(permissionToDelete).join(', ') || '—'}</strong> on{' '}
                                    <strong>{permissionToDelete.whatType}</strong>{' '}
                                    {Array.isArray(permissionToDelete.what)
                                        ? permissionToDelete.what.join(', ')
                                        : permissionToDelete.what}
                                </div>
                            )}
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};
